import { Action } from 'redux';
import { createSelector } from '@reduxjs/toolkit';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { getAppMonitor } from '../../monitor';
import { RootState } from '..';
import { resetSelectedItems } from '../actions/selectActions';
import { ThunkActionVoid } from '../actions/thunkActions';
import { clearPickingObject, toggleMemberCuttingMode } from '../slices/applicationSlice';
import { getCurrentRenderMode } from '../slices/camera/selectors';
import { setShowReferenceLines } from '../slices/visualOptionsSlice';
import { isLayoutViewActive } from './layoutModeSelectors';
import { getPickingObjectInfo } from './pickingSelectors';

// A note about terms: "select" vs "pick".
//
// Selection refers to selecting a reference line to make it the active item in the design program (the URL will change
//   to include its guid, the line's properties will be visible in the sidebar, etc.)  Whenever reference lines are
//   visible, they are probably selectable except during line picking and a few cases that force read-only display.
//
// Picking refers to picking a reference line or intersection between reference lines for the sake of some operation
//   that is currently being performed.  This is usually a short-lived state that begins via a UI control and ends as
//   soon as a line/intersection is picked or the operation is canceled.

interface ReferenceLineDisplayMode {
  type: 'none' | 'selecting' | 'picking';
  toggle?(): Action | ThunkActionVoid;
}

export const getReferenceLineDisplayMode = createSelector(
  isLayoutViewActive,
  (state: RootState) => state.application.inMemberCuttingMode,
  (state: RootState) => state.application.insertingObjectInfo,
  getPickingObjectInfo,
  getSelectedItemIdentifiers,
  (state: RootState) => state.visualOptions.showReferenceLines,

  (
    inLayoutView,
    isCuttingMember,
    insertingObjectInfo,
    pickingObjectInfo,
    selectedItems,
    referenceLinesManuallyEnabled,
  ): ReferenceLineDisplayMode => {
    if (inLayoutView) {
      return { type: 'none' };
    }

    if (isCuttingMember) {
      return { type: 'picking', toggle: toggleMemberCuttingMode };
    }

    switch (pickingObjectInfo?.type) {
      case 'pickingReferenceLine':
      case 'pickingReferenceLineIntersection':
        return {
          type: 'picking',
          toggle: clearPickingObject,
        };
    }

    switch (insertingObjectInfo?.type) {
      case 'referenceLine':
        return { type: 'picking' };

      case 'member':
        return { type: insertingObjectInfo.isBrace ? 'none' : 'picking' };
    }

    if (selectedItems.some((item) => item.type === 'referenceLine')) {
      return {
        type: 'selecting',
        toggle: resetSelectedItems,
      };
    }

    return {
      type: referenceLinesManuallyEnabled ? 'selecting' : 'none',
      toggle: () => {
        getAppMonitor().recordInteraction('Reference Lines Manually Toggled');
        return setShowReferenceLines(!referenceLinesManuallyEnabled);
      },
    };
  },
);

export const referenceLinesAreSelectable = createSelector(
  getReferenceLineDisplayMode,
  getCurrentRenderMode,
  (displayMode, renderMode) =>
    renderMode === 'design' && (displayMode.type === 'selecting' || displayMode.type === 'picking'),
);
